"use client";

import { useEffect, useState } from "react";

import { Button } from "@/app/components/ui/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/app/components/ui/carousel";
import {
  FUNNEL_FLOW,
  getFunnelStepIndex,
  type FunnelCardViewModelT,
} from "../constants/funnel-data";
import { useFinancialServiceFunnelStore } from "@/app/stores/financial-service-funnel-store";

type FunnelCardPropsT = Omit<FunnelCardViewModelT, "id">;

function FunnelCard({
  title,
  description,
  badgeLabel,
  onClick,
  href,
  target,
  rel,
  disabled,
  isClickable = false,
}: FunnelCardPropsT) {
  const hoverClass = isClickable ? "hover:bg-white/60" : "";
  const className = `flex h-full min-h-20 flex-row items-start gap-3 rounded-lg bg-white p-3 text-left transition-colors ${hoverClass} md:min-h-54 md:flex-col md:gap-0 md:p-4 ${
    disabled
      ? "pointer-events-none opacity-60"
      : isClickable
        ? "cursor-pointer"
        : ""
  }`;
  const badgeClassName = `flex h-12 w-12 shrink-0 items-center justify-center self-center rounded-lg text-xl font-semibold sm:self-start md:mb-8 md:h-14 md:w-14 md:text-2xl bg-muted/90 text-primary`;

  const content = (
    <>
      <div className={badgeClassName}>{badgeLabel}</div>
      <div className="flex min-w-0 flex-1 flex-col">
        <div>
          <h3 className="text-foreground text-base font-semibold md:text-xl">
            {title}
          </h3>
        </div>
        {description ? (
          <p className="text-foreground/80 mt-1 text-xs md:mt-2 md:text-sm">
            {description}
          </p>
        ) : null}
      </div>
    </>
  );

  if (href) {
    return (
      <a
        href={href}
        className={className}
        target={target}
        rel={rel}
        onClick={onClick}
      >
        {content}
      </a>
    );
  }

  if (onClick) {
    return (
      <button
        type="button"
        className={className}
        onClick={onClick}
        disabled={disabled}
      >
        {content}
      </button>
    );
  }

  return <div className={className}>{content}</div>;
}

export default function FunnelSteps() {
  const [carouselApi, setCarouselApi] = useState<CarouselApi | null>(null);
  const {
    currentStep,
    selectedLocation,
    selectedMake,
    selectedService,
    startFunnel,
    selectLocation,
    selectMake,
    selectService,
    goBack,
  } = useFinancialServiceFunnelStore();

  const funnelSlides = FUNNEL_FLOW.map((step) => ({
    ...step,
    cards: step.cards({
      state: {
        selectedLocation,
        selectedMake,
        selectedService,
      },
      actions: {
        selectLocation,
        selectMake,
        selectService,
      },
    }),
  }));

  const activeSlideIndex = getFunnelStepIndex(currentStep);

  const carouselOptions = {
    align: "start",
    loop: false,
    duration: 30,
    dragFree: false,
    draggable: false,
  } as const;

  useEffect(() => {
    if (!carouselApi) {
      return;
    }

    carouselApi.scrollTo(activeSlideIndex);
  }, [carouselApi, activeSlideIndex]);

  return (
    <div className="mt-8">
      <Carousel setApi={setCarouselApi} opts={carouselOptions}>
        <CarouselContent className="gap-8 md:gap-16">
          {funnelSlides.map((slide) => (
            <CarouselItem key={slide.id}>
              <div className="space-y-8 md:space-y-10">
                <div className="grid grid-cols-1 gap-4 sm:gap-4 md:grid-cols-2 md:gap-6 xl:grid-cols-4">
                  {slide.cards.map((card) => (
                    <FunnelCard key={card.id} {...card} />
                  ))}
                </div>
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
      <div className="mt-12 mb-20 flex justify-center gap-4">
        {currentStep === "intro" ? (
          <Button variant="default" onClick={startFunnel}>
            Jetzt starten
          </Button>
        ) : (
          <Button variant="outline" onClick={goBack}>
            Zurück
          </Button>
        )}
      </div>
    </div>
  );
}
